import React, {useCallback} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {Hotel, Room, RoomOffer} from '@findhotel/sapi'
import {ContentHotel} from '@findhotel/sapi/dist/types/packages/core/src/types/types'

import {Button, ButtonSize} from '@vio/atlas/Button'
import {Icon} from '@vio/atlas/Icon'
import {getAnonymousId} from '@vio/core/src/_web/anonymousId'
import {useInView} from '@vio/core/src/_web/utils/browser/hooks/useInView'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  AnalyticsContext,
  Category,
  ChargeType,
  Entity,
  Page
} from '@vio/core/src/analytics/types/Events'
import {getIsAuthenticated} from '@vio/core/src/auth/modules/selectors'
import {
  AuthenticationFlows,
  SourceComponentType
} from '@vio/core/src/auth/types/Auth'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {getLanguageCode} from '@vio/core/src/localization/services/locale'
import {Room as RoomType} from '@vio/core/src/room/types/room'

import {useLogIn} from '../../../authentication/hooks/useLogIn'
import {useShouldShowDealFreezeOnboarding} from '../../hooks/useShouldShowDealFreezeOnboarding'
import {
  DealFreezeAnalyticsContext,
  openFreezeOverlay,
  openOnboardingOverlay,
  SearchDetail,
  setAnalyticsContext
} from '../../modules/slice'
import {ComponentSource, DealFreezeOnboardingSource} from '../../types'
import {shouldShowFreezeIcon} from '../../utils'
import {writeDealFreezeStash} from '../../utils/stash'
import {getLandingUrlWithDFParam} from '../../utils/url'
import {LeadContextRequiredData} from './type'
import {useCreateAnalyticsLeadContext} from './useCreateAnalyticsLeadContext'
import {useTrackOfferView} from './useTrackButtonView'

export interface Props {
  offerId: string
  hotel: Hotel | ContentHotel
  searchDetail: SearchDetail
  /**
   * This property should be included wherever the SAPI room is available, such as in the RSP and AP room contexts.
   */
  room?: Room | RoomType
  analyticsContext: DealFreezeAnalyticsContext
  /**
   * Required from `client` package to be used to simulate redirect event and create leadContext
   */
  leadContextRequiredData?: LeadContextRequiredData
  fullWidth?: boolean
  /**
   * specifies which component CTA is rendered in
   */
  component: ComponentSource | string
  /**
   * Extra action to be performed when the button is clicked
   */
  onClick?: () => void
  /** Whether the button is disabled */
  disabled?: boolean
  /** Button size */
  size?: ButtonSize
}

export const DealFreezeButton: React.FC<Props> = ({
  room,
  fullWidth,
  offerId,
  hotel,
  searchDetail,
  analyticsContext,
  leadContextRequiredData,
  component,
  onClick,
  disabled,
  size
}) => {
  const dispatch = useDispatch()
  const language = useSelector(getLanguageCode)
  const hotelId = hotel?.objectID
  const isAuthenticated = useSelector(getIsAuthenticated)

  const roomOffer = room?.offers?.find(({id}) => id === offerId)

  const logIn = useLogIn()
  const shouldShowOnboarding = useShouldShowDealFreezeOnboarding()
  const {ref, inView} = useInView({triggerOnce: true, threshold: 0.5})
  const createAnalyticsLeadContext = useCreateAnalyticsLeadContext({
    ...leadContextRequiredData,
    offerId
  })
  useTrackOfferView({offerId, inView, analyticsContext, component})

  const anonymousId = getAnonymousId() as string

  const openOverlay = () => {
    dispatch(setAnalyticsContext(analyticsContext))

    if (roomOffer) {
      dispatch(
        openFreezeOverlay({
          offer: {
            id: roomOffer.id,
            providerCode: roomOffer.providerCode,
            links: roomOffer.links
          },
          hotel,
          searchDetail,
          room: room && [room as Room],
          component
        })
      )
    }

    createAnalyticsLeadContext()
  }

  const openOnboarding = (offer: NonNullable<typeof roomOffer>) => {
    dispatch(setAnalyticsContext(analyticsContext))
    const {providerRateType, intermediaryProvider} =
      leadContextRequiredData ?? {}

    dispatch(
      openOnboardingOverlay({
        source: DealFreezeOnboardingSource.DealFreezeButton,
        offer: {
          id: offer.id,
          providerCode: offer.providerCode,
          links: offer.links
        },
        hotel,
        searchDetail,
        room: room && [room as Room],
        component,
        analyticsContext,
        leadContextRequiredData: {providerRateType, intermediaryProvider}
      })
    )
  }

  const openSignInOverlay = (landingUrl: string) => {
    logIn({
      anonymousId,
      sourceComponent: SourceComponentType.DealFreezeButton,
      unlockHotelId: hotelId,
      flow: AuthenticationFlows.DealFreeze,
      onAuthSuccessLandingUrl: landingUrl
    })
  }

  const trackOfferClick = useCallback(() => {
    const {offerContext, priceContext, leadContext, hotelContext} =
      analyticsContext
    const availableAnalyticsContexts = {
      ...(offerContext && {
        [AnalyticsContext.OfferContext]: {
          ...offerContext,
          chargeType: [ChargeType.DealFreeze]
        }
      }),
      ...(priceContext && {[AnalyticsContext.PriceContext]: priceContext}),
      ...(leadContext && {[AnalyticsContext.LeadContext]: leadContext}),
      ...(hotelContext && {[AnalyticsContext.HotelContext]: hotelContext})
    }

    dispatch(
      trackEvent({
        category: Category.User,
        entity: Entity.DealFreezeOfferButton,
        action: Action.Clicked,
        analyticsContext: {...availableAnalyticsContexts},
        page: Page.Accommodation,
        component
      })
    )
  }, [analyticsContext, component, dispatch])

  const onButtonClick = (event: React.SyntheticEvent) => {
    event.preventDefault()
    event.stopPropagation()
    onClick?.()
    trackOfferClick()

    if (!isAuthenticated) {
      storeSelectedOffer()
      if (!roomOffer) {
        openSignInOverlay(getLandingUrlWithDFParam())
        return
      }
    }

    if (roomOffer && shouldShowOnboarding()) {
      openOnboarding(roomOffer)
      return
    }

    openOverlay()
  }

  // Stores the selected offer for the post-auth resume.
  const storeSelectedOffer = () => {
    writeDealFreezeStash({
      analyticsContext,
      roomOffer: roomOffer as RoomOffer | undefined,
      hotel,
      searchDetail,
      room: room as Room | undefined,
      component,
      offerId,
      leadContextRequiredData: leadContextRequiredData ?? {}
    })
  }

  const showFreezeIcon = shouldShowFreezeIcon(language)

  return (
    <Button
      dataId="FreezeDeal"
      ref={ref}
      iconEnd={showFreezeIcon && <Icon name={'Ice'} />}
      fullWidth={fullWidth}
      onClick={onButtonClick}
      variant="secondary"
      disabled={disabled}
      size={size}
    >
      <FormattedMessageWrapper
        id="dealFreeze.freezeDeal"
        defaultMessage="Freeze deal"
      />
    </Button>
  )
}
